import * as Sentry from "@sentry/react";
import {
  useEffect,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "./sentry-router-deps";
import { isExpectedOperationalError } from "./sentry-noise";
import { reloadSuppressed, isChunkLoadError } from "./reloadGuard";
import { hasAnalyticsConsent } from "@/lib/consent";

// True while the document is being torn down (real navigation / reload / tab
// close — SPA route changes never fire pagehide). In-flight fetches abort at
// that moment by design, and the resulting transport "errors"
// (FunctionsFetchError "Failed to send a request…", Failed to fetch, AbortError)
// are the browser cancelling work, not bugs (Sentry CR-DASHBOARD-1F: every
// event lined up with leaving the scanner mid-batch — which the resume marker
// now recovers on return). Reset on pageshow so a bfcache restore reports
// normally again. Exported setter for tests.
let pageUnloading = false;
export function markPageUnloading(value = true): void {
  pageUnloading = value;
}

export function initSentry() {
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", () => markPageUnloading(true));
    window.addEventListener("beforeunload", () => markPageUnloading(true));
    window.addEventListener("pageshow", () => markPageUnloading(false));
  }
  // Error monitoring is treated as a strictly-necessary / legitimate-interest
  // function, so Sentry itself always runs (we must be able to capture crashes
  // even before a consent choice). Session Replay, however, records the user's
  // screen and is non-essential tracking under Quebec Law 25 / PIPEDA, so it is
  // gated on consent: only loaded once the visitor has accepted. Error capture
  // is unaffected either way — only the attached replay recording is held back.
  const replayConsented = hasAnalyticsConsent();
  const integrations: any[] = [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.feedbackIntegration({
      colorScheme: "system",
      buttonLabel: "Report a Bug",
      submitButtonLabel: "Send Report",
      formTitle: "Report a Bug",
      messagePlaceholder: "What happened? What did you expect?",
      showBranding: false,
      autoInject: false,
    }),
  ];
  if (replayConsented) {
    integrations.push(Sentry.replayIntegration());
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || "https://698a9c480c98d8f8aa896d3a2ce21a56@o4510455980163073.ingest.us.sentry.io/4510456041963520",
    enabled: true,
    tracesSampleRate: 0.1,
    // Sample dashboard navigations more aggressively so we always have
    // a trace to look at when an admin/dash page errors. Other routes
    // fall back to the global tracesSampleRate above.
    tracesSampler: ({ name, attributes, parentSampled }) => {
      if (parentSampled !== undefined) return parentSampled;
      const op = attributes?.["sentry.op"];
      if (op === "pageload" || op === "navigation") {
        if (typeof name === "string" && name.startsWith("/dash")) return 1.0;
      }
      return 0.1;
    },
    replaysSessionSampleRate: 0,
    // Only attach an on-error replay recording when the visitor has consented;
    // 0 otherwise so no session is ever recorded pre-consent. Error events
    // themselves still report at full fidelity regardless.
    replaysOnErrorSampleRate: replayConsented ? 1.0 : 0,
    integrations,
    environment: import.meta.env.MODE,
    beforeBreadcrumb(breadcrumb) {
      // Scrub PII from network breadcrumbs
      if (breadcrumb.category === "xhr" || breadcrumb.category === "fetch") {
        delete breadcrumb.data?.request_body;
      }
      return breadcrumb;
    },
    beforeSend(event) {
      return processSentryEvent(event);
    },
  });
  sentryDsnLoaded = Boolean(
    import.meta.env.VITE_SENTRY_DSN ||
      "https://698a9c480c98d8f8aa896d3a2ce21a56@o4510455980163073.ingest.us.sentry.io/4510456041963520",
  );
}

// Tracks whether replay has been wired in, so a late opt-in adds it exactly
// once without re-initializing the whole Sentry client (which would disrupt the
// live error pipeline).
let replayAdded = false;

/**
 * Start Sentry Session Replay after a late consent opt-in.
 *
 * Called from the cr:consent listener when the visitor accepts. No-ops if
 * replay was already loaded at init (consent existed on page load) or if
 * consent is somehow not granted. Adds the integration to the running client
 * rather than re-calling Sentry.init, so error reporting is never interrupted.
 */
export function enableSentryReplayOnConsent(): void {
  if (replayAdded) return;
  if (!hasAnalyticsConsent()) return;
  const client = Sentry.getClient();
  if (!client) return;
  // If replay was already configured at init, getIntegrationByName finds it.
  if (client.getIntegrationByName?.("Replay")) {
    replayAdded = true;
    return;
  }
  client.addIntegration?.(Sentry.replayIntegration());
  replayAdded = true;
}

// Read the HTTP status of the most recent `/functions/v1/` fetch/xhr breadcrumb.
// Used as a fail-open backstop to classify a raw FunctionsHttpError (whose event
// carries no status of its own). Returns null when no such breadcrumb is found —
// callers must treat null as "unknown, keep the event", never as "drop".
export function functionsHttpStatusFromBreadcrumbs(event: any): number | null {
  const crumbs = event?.breadcrumbs;
  if (!Array.isArray(crumbs)) return null;
  for (let i = crumbs.length - 1; i >= 0; i--) {
    const c = crumbs[i];
    if (!c || (c.category !== "fetch" && c.category !== "xhr")) continue;
    const d = (c.data ?? {}) as Record<string, unknown>;
    const status =
      typeof d.status_code === "number"
        ? d.status_code
        : typeof d.status === "number"
          ? d.status
          : null;
    if (status === null) continue;
    if (/\/functions\/v1\//.test(String(d.url ?? ""))) return status;
  }
  return null;
}

// Exported for tests · this is the exact pipeline used by Sentry's beforeSend.
// Returns the (possibly mutated) event, or null to drop it.
export function processSentryEvent(
  event: any,
): any | null {
  // Strip any accidental PII from event extras/context
  if (event.extra) {
    delete event.extra["api_key"];
    delete event.extra["auth_header"];
    delete event.extra["password"];
    delete event.extra["authorization"];
  }
  if (event.request?.headers) {
    delete event.request.headers["authorization"];
    delete event.request.headers["Authorization"];
    delete event.request.headers["cookie"];
    delete event.request.headers["Cookie"];
  }
  // Never let an email address through on the user context · id only.
  if (event.user) {
    const { id } = event.user;
    event.user = id ? { id } : undefined;
  }
  // Drop noise from third-party SDKs that schedule internal timers we
  // can't cancel on unmount (HERE Maps, Cloudflare Turnstile).
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  const isThirdPartyNoise = frames.some((f) => {
    const fn = f.filename ?? "";
    return (
      fn.includes("js.api.here.com") ||
      fn.includes("mapsjs-core.js") ||
      fn.includes("challenges.cloudflare.com") ||
      fn.includes("turnstile")
    );
  });
  if (isThirdPartyNoise) return null;
  // Browser-extension-injected globals (not our code, not fixable by us).
  // "externalProg" is a known content-script artifact (CR-DASHBOARD-3B).
  const exValues = (event.exception?.values ?? []) as Array<{ type?: string; value?: string }>;
  const mentions = (needle: string) =>
    exValues.some((v) => (v.value ?? "").toLowerCase().includes(needle));
  if (mentions("externalprog")) return null;
  // supabase-js serializes auth token refresh through the Web Locks API
  // (autoRefreshToken + persistSession). When a second tab or request preempts the
  // held lock, the browser rejects the waiter with
  // "AbortError: Lock was stolen by another request" (Sentry CR-DASHBOARD-3K). It is
  // benign multi-tab contention — the refresh simply retries — never a bug and not
  // actionable, so drop it unconditionally. The holder-side twin of the same
  // arbitration ('Lock "lock:sb-…-auth-token" was released because another
  // request stole it', CR-DASHBOARD-3J) is equally benign.
  if (mentions("lock was stolen")) return null;
  if (mentions("another request stole it")) return null;
  // In-app webviews (TikTok/Instagram/Gmail iOS, etc.) inject host-bridge
  // scripts that probe window.webkit.messageHandlers; when the bridge is
  // half-present the probe throws inside THEIR injected code
  // (CR-DASHBOARD-3M, seen on /signup). Not our code, not fixable by us.
  if (mentions("webkit.messagehandlers")) return null;
  // Transport errors while the page is being torn down are the browser
  // cancelling in-flight work (CR-DASHBOARD-1F) — never a bug. Scoped to the
  // unload window so genuine network failures still report.
  if (pageUnloading) {
    const abortish =
      exValues.some((v) =>
        ["FunctionsFetchError", "AbortError", "NetworkError"].includes(v.type ?? ""),
      ) ||
      mentions("failed to send a request") ||
      mentions("failed to fetch") ||
      mentions("load failed") ||
      mentions("networkerror") ||
      mentions("signal has been aborted") ||
      mentions("signal is aborted");
    if (abortish) return null;
  }
  // Stale-chunk / failed-lazy-import errors (Sentry CIPHER-AND-ROW-2T,
  // CR-DASHBOARD-29). A long-lived tab holds old hashed chunk URLs; the next lazy
  // route/section import 404s, or resolves a namespace with no default export —
  // which surfaces either as a chunk-load error, lazyRetry's promoted "stale chunk
  // after deploy" Error, or a raw TypeError reading the named export off an
  // undefined namespace ("evaluating 'e.HowItWorksSection'"). lazyRetry already
  // reloads-once to self-heal a genuine post-deploy stale chunk; what still reaches
  // Sentry is the transient drop it re-throws when a reload is intentionally NOT
  // triggered — i.e. the tab is backgrounded/offline/holding work (reloadSuppressed)
  // or we are unloading. Scope the drop to exactly those non-active conditions:
  // a genuinely broken chunk hitting an ACTIVE user (visible + online) is not
  // suppressed here, so it still reports.
  const firstType = exValues[0]?.type ?? "";
  const isStaleLazyChunk =
    exValues.some((v) => isChunkLoadError(v.value ?? "")) ||
    mentions("stale chunk after deploy") ||
    (firstType === "TypeError" &&
      exValues.some((v) => {
        const m = v.value ?? "";
        // Named-export read off an undefined dynamic-import namespace:
        //   Safari  "undefined is not an object (evaluating 'e.HowItWorksSection')"
        //   Chrome  "Cannot read properties of undefined (reading 'HowItWorksSection')"
        // Case-sensitive on purpose: the leading '[A-Z]' PascalCase constraint is
        // the guardrail that keeps real bugs like "reading 'id'" reporting. Chrome
        // and Safari emit these messages with stable casing.
        return (
          /undefined is not an object \(evaluating '[\w$]{1,4}\.[A-Z][A-Za-z0-9]+'\)/.test(m) ||
          /Cannot read properties of undefined \(reading '[A-Z][A-Za-z0-9]+'\)/.test(m)
        );
      }));
  if (isStaleLazyChunk && (pageUnloading || reloadSuppressed())) return null;
  // Expected operational states (plan/usage/rate limits, quota, billing,
  // 402/429) are normal UX conditions, not bugs. Drop them so they never
  // create a Sentry issue. The call site still surfaces its own UI message.
  if (isExpectedOperationalError(event)) return null;
  // Edge-function call failures (CR-DASHBOARD-11). `invokeEdge()` classifies
  // these at the source — where the Response + status still exist — into two
  // exception types:
  //   - EdgeFunctionExpectedError: a deliberate 4xx business state (auth /
  //     validation / no-billing-account) → drop; it is normal UX, and the call
  //     site already surfaces its message.
  //   - EdgeFunctionError: a 5xx, or a 4xx with no app body → KEEP; it now carries
  //     "<fn> <status>", so it is actionable rather than a mute.
  if (exValues.some((v) => (v.type ?? "") === "EdgeFunctionExpectedError")) {
    return null;
  }
  // Backstop for raw supabase-js FunctionsHttpError from call sites not yet on
  // invokeEdge. The event carries no status, but Sentry's fetch breadcrumb does.
  // Drop ONLY when a breadcrumb positively shows a 4xx for a `/functions/v1/`
  // call; keep 5xx AND keep unknowns (fail-open) so an outage is never muted —
  // the exact trap the FMCSA "transient" suppression fell into.
  if (exValues.some((v) => (v.type ?? "") === "FunctionsHttpError")) {
    const status = functionsHttpStatusFromBreadcrumbs(event);
    if (status !== null && status >= 400 && status < 500) return null;
  }
  recordSentryEvent({
    at: Date.now(),
    eventId: event.event_id,
    level: event.level,
    type: event.exception?.values?.[0]?.type,
    message:
      event.exception?.values?.[0]?.value ||
      (typeof event.message === "string" ? event.message : undefined),
  });
  return event;
}

// ── In-app status tracking (admin widget) ─────────────────────────────────
let sentryDsnLoaded = false;

export interface SentryLastEvent {
  at: number;
  eventId?: string;
  level?: string;
  type?: string;
  message?: string;
}

let lastEvent: SentryLastEvent | null = null;
const listeners = new Set<(snap: SentryStatusSnapshot) => void>();

export interface SentryStatusSnapshot {
  dsnLoaded: boolean;
  environment: string;
  lastEvent: SentryLastEvent | null;
}

function snapshot(): SentryStatusSnapshot {
  return {
    dsnLoaded: sentryDsnLoaded,
    environment: import.meta.env.MODE,
    lastEvent,
  };
}

function recordSentryEvent(ev: SentryLastEvent) {
  lastEvent = ev;
  const snap = snapshot();
  listeners.forEach((l) => {
    try {
      l(snap);
    } catch {
      /* listener errors must not break Sentry pipeline */
    }
  });
}

/** Subscribe to Sentry status changes. Returns an unsubscribe fn. */
export function subscribeSentryStatus(
  listener: (snap: SentryStatusSnapshot) => void,
): () => void {
  listeners.add(listener);
  listener(snapshot());
  return () => {
    listeners.delete(listener);
  };
}

/** One-shot read of current Sentry status, for SSR-safe initial state. */
export function getSentryStatus(): SentryStatusSnapshot {
  return snapshot();
}

/** Set authenticated user context on Sentry · user ID only, no email (SOC 2 MOD-2 / PII) */
export function setSentryUser(user: { id: string; email?: string } | null) {
  if (user) {
    // Do not send email to Sentry · user ID is sufficient for incident correlation
    Sentry.setUser({ id: user.id });
  } else {
    Sentry.setUser(null);
  }
}

/** Tag events with the active tenant · the company uuid is a tenant identifier,
 *  not PII (same stance as the id-only user context above), and it enables
 *  cross-tenant triage: "is this error one tenant or everyone?" */
export function setSentryCompany(companyId: string | null | undefined) {
  // setTag(undefined) clears the tag, so sign-out / no-company wipes it.
  Sentry.setTag("company_id", companyId ?? undefined);
}

/** Add a worker-api breadcrumb for tracing */
export function addWorkerBreadcrumb(opts: {
  method: string;
  endpoint: string;
  agentId?: string;
  companyId?: string;
  status: number;
  ok: boolean;
}) {
  Sentry.addBreadcrumb({
    category: "worker-api",
    message: `${opts.method} ${opts.endpoint}`,
    data: {
      agent_id: opts.agentId,
      company_id: opts.companyId,
      status: opts.status,
    },
    level: opts.ok ? "info" : "error",
  });
}

/** Sentry ErrorBoundary component for self-healing route recovery */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

export { Sentry };
